import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

const SCORES = [1, 2, 3, 4, 5] as const;

// Literal translation calls only: the static i18n guard forbids passing variables.
export const scoreLabel = (t: TFunction, score: number): string => {
  switch (score) {
    case 1:
      return t('Very dissatisfied');
    case 2:
      return t('Dissatisfied');
    case 3:
      return t('Fair');
    case 4:
      return t('Satisfied');
    case 5:
      return t('Excellent');
    default:
      return '';
  }
};

export const scoreMeaning = (t: TFunction, score: number): string => {
  switch (score) {
    case 1:
      return t('Service not compliant, a redo is needed');
    case 2:
      return t('Service incomplete, several points need redoing');
    case 3:
      return t('Service carried out, with minor reservations');
    case 4:
      return t('Service matches the request');
    case 5:
      return t('Compliant service, on time, space restored');
    default:
      return '';
  }
};

interface StarRatingProps {
  value: number;
  onChange?: (score: number) => void;
  readOnly?: boolean;
  size?: 'sm' | 'lg';
  // Extra layout classes on the row (e.g. spread the stars across the card).
  className?: string;
}

const StarRating = ({ value, onChange, readOnly = false, size = 'sm', className = '' }: StarRatingProps) => {
  const { t } = useTranslation();
  const dimension = size === 'lg' ? 'w-10 h-10' : 'w-5 h-5';

  return (
    <div
      className={`flex items-center ${size === 'lg' ? 'gap-4' : 'gap-1'} ${className}`.trim()}
      role={readOnly ? 'img' : undefined}
      aria-label={readOnly ? scoreLabel(t, value) : undefined}
    >
      {SCORES.map((score) => {
        const filled = score <= value;
        const icon = (
          <Star
            className={`${dimension} ${filled ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
            strokeWidth={1.5}
            aria-hidden="true"
          />
        );
        if (readOnly) return <span key={score}>{icon}</span>;
        return (
          <button
            key={score}
            type="button"
            aria-label={scoreLabel(t, score)}
            aria-pressed={score === value}
            onClick={() => onChange?.(score)}
            className="rounded-md p-0.5 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {icon}
          </button>
        );
      })}
    </div>
  );
};

export default StarRating;
